import { FC, useCallback } from 'react';
import intl from 'react-intl-universal';
import {
  Tag,
  TableRow,
  tailwindOverride,
  IconButton,
  Tooltip,
} from '@getsynapse/design-system';
import TableAvatar from 'Molecules/TableAvatar';
import { LDUser } from 'utils/customTypes';
import { PATHS, USER_STATUS } from 'utils/constants';
import { COLUMN_WIDTHS } from './constants';

interface MemberRowProps {
  member: LDUser;
  bgColor: string;
  onRemoveMember: (memberId: string) => void;
}

const MemberRow: FC<MemberRowProps> = ({ member, bgColor, onRemoveMember }) => {
  const getEmploymentTypeLabel = useCallback(() => {
    const employmentType = member.data.employmentType;
    if (!employmentType) return '';
    return intl.get(`TEAMS.EMPLOYMENT_TYPE.${employmentType.toUpperCase()}`);
  }, [member.data.employmentType]);

  const getLocationLabel = useCallback(() => {
    const province = member.data.province;
    const country = member.country_iso_3166_1_alpha_2_code;
    const countryLabel = country ? intl.get(`COUNTRIES.${country}`) : '';
    if (!province && !country) return '';
    let locationLabel = '';
    if (province) {
      locationLabel = province;
    }
    if (countryLabel && province) {
      locationLabel = `${province}, ${countryLabel}`;
    }
    if (countryLabel && !province) {
      locationLabel = countryLabel;
    }
    return locationLabel;
  }, [member.data.province, member.country_iso_3166_1_alpha_2_code]);

  const statusTagColors = {
    [USER_STATUS.REGISTERED]: {
      className: 'bg-success-lightest',
      textClassName: 'text-success-darker',
    },
    [USER_STATUS.INVITED]: {
      className: 'bg-warning-lightest',
      textClassName: 'text-warning-darker',
    },
    [USER_STATUS.REGISTERED_DISABLED]: {
      className: 'bg-neutral-lighter-two',
      textClassName: 'text-neutral-darker',
    },
    [USER_STATUS.INVITED_DISABLED]: {
      className: 'bg-neutral-lighter-two',
      textClassName: 'text-neutral-darker',
    },
  };

  return (
    <TableRow
      id={member.id}
      className={tailwindOverride('group cursor-pointer', bgColor)}
      cells={[
        {
          content: (
            <div className='min-w-0 overflow-hidden'>
              <TableAvatar
                user={member}
                to={`${PATHS.USER_PAGE}/${member.id}`}
              />
            </div>
          ),
          className: tailwindOverride('leading-6 min-w-0'),
          style: { width: COLUMN_WIDTHS.name },
        },
        {
          content: (
            <div className='min-w-0 truncate'>{member.data.jobTitle}</div>
          ),
          className: tailwindOverride('leading-4 min-w-0'),
          style: { width: COLUMN_WIDTHS.jobTitle },
        },
        {
          content: (
            <div className='min-w-0 truncate'>{getEmploymentTypeLabel()}</div>
          ),
          className: 'leading-4 min-w-0',
          style: { width: COLUMN_WIDTHS.employmentType },
        },
        {
          content: <div className='min-w-0 truncate'>{getLocationLabel()}</div>,
          className: 'leading-4 min-w-0',
          style: { width: COLUMN_WIDTHS.location },
        },
        {
          content: (
            <Tag
              label={intl.get(
                `USERS_PAGE.TABLE.USER_STATUS.${member.status.toUpperCase()}`
              )}
              className={statusTagColors[member.status].className}
              textClassName={tailwindOverride(
                'font-medium',
                statusTagColors[member.status].textClassName
              )}
            />
          ),
          className: 'leading-4 min-w-0',
          style: { width: COLUMN_WIDTHS.accountStatus },
        },
        {
          content: (
            <div className='flex h-full justify-center align-middle'>
              <Tooltip
                trigger={
                  <IconButton
                    name='close-circle'
                    className='text-neutral text-lg hover:cursor-pointer'
                    onClick={() => onRemoveMember(member.id)}
                  />
                }
                contentProps={{
                  className: 'px-3 py-2 text-sm z-50 absolute',
                }}
                position='topCenter'
                openMode='hover2'
                timeout={0}
                showPopper
                usePortal
              >
                {intl.get('REMOVE')}
              </Tooltip>
            </div>
          ),
          style: {
            width: COLUMN_WIDTHS.actions,
          },
        },
      ]}
    />
  );
};

export default MemberRow;
